import { memo, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Flex, IconWarning } from '@zillow/constellation';
import { Box, IconButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { LoadingTag } from '@zg-rentals/loading-components';

import { RMWContext } from '../../../../providers/RMWContext';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../../../hooks/queries/useGetPropertyDetailsOverview';

import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import EditPropertyTypeMenu from '../../../../components/EditPropertyTypeMenu';
import AddressDetails from '../AddressDetails';

import MediumIllustrationHouse1x from '../../../../images/MediumIllustrationHouse@1x.png';
import MediumIllustrationHouse2x from '../../../../images/MediumIllustrationHouse@2x.png';

import { StyledLink, StyledMenuSpacer, StyledStatusSpacer } from './StyledComponents';
import { propertyPhotoContainerStyles } from './styles';

import type { GetUserPropertyOverviewResponseWebDto } from '@zg-rentals/rental-manager-env';

type Props = {
  listingAlias: string;
  onLoadComplete?: () => void;
};

const PropertyCard = ({ listingAlias, onLoadComplete }: Props) => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const tagSize = isScreenDesktop ? 'md' : 'sm';

  const { data, isLoading: isPropertyDetailsOverviewLoading } = useGetPropertyDetailsOverview(listingAlias);
  const { isLoading: isUseWebAnalyticsLoading, data: webAnalyticsData } =
    useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
      {
        listingAlias,
        includeLeases: false,
      },
      { enabled: Boolean(listingAlias) },
    );

  const propertyDetailsOverviewData = data as GetUserPropertyOverviewResponseWebDto;

  useEffect(() => {
    if (webAnalyticsData) {
      dispatch(PropertiesActions.updateWebAnalytics(webAnalyticsData));
    }
  }, [webAnalyticsData]);

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }
  }, [propertyDetailsOverviewData]);

  useEffect(() => {
    if (isPropertyDetailsOverviewLoading === false && isUseWebAnalyticsLoading === false && onLoadComplete) {
      onLoadComplete();
    }
  }, [isPropertyDetailsOverviewLoading, isUseWebAnalyticsLoading]);

  return (
    <div data-testid="DashboardPropertyCard">
      <Flex display="flex" justifyContent={{ default: 'space-between', lg: 'start' }} alignItems="center">
        <Flex display="flex" justifyContent={{ default: 'space-between', lg: 'start' }} alignItems="center">
          <StyledLink to={paths.propertiesPage}>
            <IconButton
              emphasis="outlined"
              shape="circle"
              icon={<IconChevronLeftFilled />}
              size="sm"
              title="Back to properties"
            />
          </StyledLink>
          <Box
            data-loading={isPropertyDetailsOverviewLoading || null}
            css={{
              backgroundSize: propertyDetailsOverviewData?.photoUrl ? 'cover' : '76px',
              ...propertyPhotoContainerStyles,
            }}
            style={{
              ...(isPropertyDetailsOverviewLoading
                ? {}
                : {
                    backgroundImage: propertyDetailsOverviewData?.photoUrl
                      ? `url(${propertyDetailsOverviewData?.photoUrl})`
                      : isScreenDesktop
                        ? `url(${MediumIllustrationHouse2x})`
                        : `url(${MediumIllustrationHouse1x})`,
                  }),
            }}
          ></Box>
          <Flex
            display="flex"
            flexDirection={{ default: 'column', lg: 'row' }}
            alignSelf={{ default: 'flex-start', lg: 'center' }}
          >
            <AddressDetails address={propertyDetailsOverviewData?.address} />
            <StyledStatusSpacer size={tagSize}>
              <LoadingTag
                size={tagSize}
                tone={
                  isPropertyDetailsOverviewLoading
                    ? 'gray'
                    : getTagTypeByKey(STATUS_KEY?.PROPERTY_DETAILS_OVERVIEW, propertyDetailsOverviewData?.status)
                }
                icon={Boolean(propertyDetailsOverviewData?.status === 'UNABLE TO PUBLISH') && <IconWarning />}
                isLoading={isPropertyDetailsOverviewLoading}
                loadingWidth="5rem"
              >
                {propertyDetailsOverviewData?.status}
              </LoadingTag>
            </StyledStatusSpacer>
          </Flex>
        </Flex>
        <StyledMenuSpacer marginLeft={{ default: 'xs', lg: 'sm' }}>
          <EditPropertyTypeMenu />
        </StyledMenuSpacer>
      </Flex>
    </div>
  );
};

export default memo(PropertyCard);
